import { PhmsBrandMarkComponent } from '../../phms-brand-mark/phms-brand-mark.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { EzBaseComponent } from '@template-ui/components/ez-base.component';
import { NavItem } from '../../../../core/models/nav-item.model';
import {
  materialNavigationIcon,
  primeNavigationIcon,
} from '@app/core/models/navigation-icon';
import { AppRoutePrefixService } from '@app/core/services/app-route-prefix.service';
import { TerminologyService } from '@app/shared/services/terminology.service';

export type SideRailNavMode = 'rail' | 'expanded';

@Component({
  selector: 'app-side-rail-nav',
  imports: [PhmsBrandMarkComponent, CommonModule, RouterModule, MatIconModule],
  templateUrl: './side-rail-nav.component.html',
  styleUrls: ['./side-rail-nav.component.scss'],
  host: {
    class: 'app-side-rail-nav',
  },
})
export class SideRailNavComponent extends EzBaseComponent implements OnChanges {
  private readonly appRoutePrefix = inject(AppRoutePrefixService);
  private readonly terminology = inject(TerminologyService);
  /** Terminology configurations (code + locale) already requested for items that carry a usage. */
  private readonly requestedTerminology = new Set<string>();

  @Input() items: NavItem[] = [];
  @Input() mode: SideRailNavMode = 'rail';
  @Input() offsetTop = 0;
  @Input() activeRoute?: string;

  @Output() navItemSelected = new EventEmitter<NavItem>();
  @Output() modeChanged = new EventEmitter<SideRailNavMode>();

  activeItem?: NavItem;
  readonly primeIcon = primeNavigationIcon;
  readonly materialIcon = materialNavigationIcon;

  get isCollapsed(): boolean {
    return this.mode === 'rail';
  }

  constructor() {
    super();

    effect(() => {
      const nav = this.navigationEndSignal();
      if (nav) {
        this.highlightActiveItem();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.requestTerminology(this.items);
    }

    if (changes['items'] || changes['activeRoute']) {
      this.highlightActiveItem();
    }
  }

  toggleMode(): void {
    this.mode = this.mode === 'rail' ? 'expanded' : 'rail';
    this.modeChanged.emit(this.mode);
  }

  hoveredItem?: NavItem;
  flyoutStyle: { [key: string]: string } = {};
  /** Item whose control is being refocused by Escape; that focusin must not reopen the menu. */
  private focusReturningTo?: NavItem;

  onItemEnter(item: NavItem, event: Event, depth = 0): void {
    this.openFlyout(item, event.currentTarget as HTMLElement | null, depth);
  }

  /** Keyboard focus opens a flyout-presentation item's menu the way hover does (#1530 AC4). */
  onItemFocusIn(item: NavItem, event: FocusEvent, depth = 0): void {
    if (!this.isFlyoutPresentation(item) || this.focusReturningTo === item) {
      return;
    }

    this.openFlyout(item, event.currentTarget as HTMLElement | null, depth);
  }

  /** Closes the menu only once focus has left both the item and its flyout. */
  onItemFocusOut(item: NavItem, event: FocusEvent): void {
    if (this.hoveredItem !== item || !this.isFlyoutPresentation(item)) {
      return;
    }

    const container = event.currentTarget as HTMLElement | null;
    const next = event.relatedTarget as Node | null;
    if (container && next && container.contains(next)) {
      return;
    }

    this.hoveredItem = undefined;
  }

  /** Escape closes the open flyout and returns focus to the item's control. */
  onItemEscape(item: NavItem, event: Event): void {
    if (this.hoveredItem !== item) {
      return;
    }

    this.hoveredItem = undefined;
    this.returnFocus(item, event.currentTarget as HTMLElement | null);
  }

  /**
   * Flyout rule: collapsed rails show a flyout for every root item and for any parent (the
   * pre-existing rule); a flyout-presentation parent shows its menu in both modes.
   */
  shouldShowFlyout(item: NavItem, depth: number): boolean {
    return (
      this.isFlyoutPresentation(item) ||
      (this.isCollapsed && (item.hasChildren || depth === 0))
    );
  }

  isFlyoutPresentation(item: NavItem): boolean {
    return item.presentation === 'flyout';
  }

  onItemLeave(item: NavItem, event?: MouseEvent): void {
    if (this.hoveredItem !== item) {
      return;
    }

    if (
      this.isFlyoutPresentation(item) &&
      this.containsFocus(event?.currentTarget as HTMLElement | null)
    ) {
      return;
    }

    this.hoveredItem = undefined;
  }

  onFlyoutLeave(item?: NavItem, event?: MouseEvent): void {
    if (item && this.isFlyoutPresentation(item)) {
      const flyout = event?.currentTarget as HTMLElement | null;
      if (
        this.containsFocus(
          flyout?.closest<HTMLElement>('.side-rail-nav__item-container') ??
            null,
        )
      ) {
        return;
      }
    }

    this.hoveredItem = undefined;
  }

  onItemClick(item: NavItem, event?: MouseEvent): void {
    if (
      item.hasChildren &&
      !this.isCollapsed &&
      !this.isFlyoutPresentation(item)
    ) {
      item.expanded = !item.expanded;
    }

    if (item.hasChildren && this.isFlyoutPresentation(item)) {
      const control = event?.currentTarget as HTMLElement | null;
      this.openFlyout(
        item,
        control?.closest<HTMLElement>('.side-rail-nav__item-container') ?? null,
        0,
      );
    }

    if (!item.hasChildren) {
      this.closeMenuAfterSelection(event);
    }

    this.activeItem = item;
    this.navItemSelected.emit(item);
  }

  isRouterItem(item: NavItem): boolean {
    return item.isThisApp || this.appRoutePrefix.isAppRoute(item.route);
  }

  routerLinkFor(item: NavItem): string | null {
    return this.appRoutePrefix.stripRoutePrefix(item.route);
  }

  /** True when the item's route is the page currently shown (query string and fragment ignored). */
  isCurrentRoute(item: NavItem): boolean {
    const link = this.routerLinkFor(item);
    if (!link) {
      return false;
    }

    const current = (this.activeRoute ?? this.router.url)
      .split('#')[0]
      .split('?')[0];
    return link === current;
  }

  /** Rendered label: the item's terminology usage when it carries one, otherwise its display name. */
  labelFor(item: NavItem): string {
    return item.terminology
      ? this.terminology.resolveUsage(item.terminology)
      : item.displayName;
  }

  getItemHref(item: NavItem): string | null {
    if (this.isRouterItem(item) || item.hasChildren) {
      return null;
    }

    if (item.hasExtension && item.url) {
      return item.url;
    }

    return item.url ?? item.route ?? null;
  }

  highlightActiveItem(): void {
    const currentRoute = (this.activeRoute ?? this.router.url).split('#')[0];
    this.activeItem = undefined;
    this.updateActiveState(this.items, currentRoute);
  }

  private openFlyout(
    item: NavItem,
    host: HTMLElement | null,
    depth: number,
  ): void {
    if (!this.shouldShowFlyout(item, depth)) {
      this.hoveredItem = undefined;
      return;
    }

    if (!host) {
      return;
    }

    const rect = host.getBoundingClientRect();
    const top = rect.top + rect.height / 2;
    const left = rect.right + 8;

    this.flyoutStyle = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform: 'translateY(-50%)',
    };
    this.hoveredItem = item;
  }

  /** Activating a menu link closes a flyout-presentation menu and hands focus back to its item. */
  private closeMenuAfterSelection(event?: MouseEvent): void {
    const parent = this.hoveredItem;
    if (!parent || !this.isFlyoutPresentation(parent)) {
      return;
    }

    const link = event?.currentTarget as HTMLElement | null;
    this.hoveredItem = undefined;
    this.returnFocus(
      parent,
      link?.closest<HTMLElement>('.side-rail-nav__item-container') ?? null,
    );
  }

  /** Focuses the item's control without letting that focusin reopen the menu. */
  private returnFocus(item: NavItem, container: HTMLElement | null): void {
    const control = container?.querySelector<HTMLElement>(
      '.side-rail-nav__button, .side-rail-nav__link',
    );
    if (!control) {
      return;
    }

    // focus() dispatches focusin synchronously; the guard keeps that from reopening the menu.
    this.focusReturningTo = item;
    try {
      control.focus();
    } finally {
      this.focusReturningTo = undefined;
    }
  }

  private containsFocus(element: HTMLElement | null): boolean {
    const active = element?.ownerDocument.activeElement;
    return !!element && !!active && element.contains(active);
  }

  /** Requests each distinct terminology configuration the items reference, once per component life. */
  private requestTerminology(items: NavItem[] | undefined): void {
    for (const item of items ?? []) {
      const usage = item.terminology;
      if (usage) {
        const key = `${usage.configurationCode} ${usage.localeCode ?? 'en-us'}`;
        if (!this.requestedTerminology.has(key)) {
          this.requestedTerminology.add(key);
          this.terminology
            .loadUsage(usage)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      }

      if (item.children?.length) {
        this.requestTerminology(item.children);
      }
    }
  }

  private updateActiveState(items: NavItem[], route: string): boolean {
    let found = false;

    for (const item of items) {
      item.isActive = false;

      let hasActiveChild = false;
      if (item.children?.length) {
        hasActiveChild = this.updateActiveState(item.children, route);
      }

      if (item.route?.endsWith(route)) {
        item.isActive = true;
        this.activeItem = item;
        found = true;
      }

      if (hasActiveChild) {
        item.expanded = true;
        found = true;
      }
    }

    return found;
  }
}
